export const WINDOWS_INSTALLER_FILENAME = "NolaDesktop-Setup.exe";
export const MAC_INSTALLER_FILENAME = "NolaDesktop-universal.dmg";

export type DesktopInstallerPlatform = "mac" | "windows";

/** 2026-10-30 23:59:59 JST までダウンロード可。10/31 00:00:00 JST 以降は不可。 */
export const DESKTOP_BETA_DOWNLOAD_DEADLINE_MS = Date.parse("2026-10-31T00:00:00+09:00");

export const DESKTOP_BETA_DOWNLOAD_CLOSED = "desktop beta download closed";

type FetchInit = {
  method?: string;
  cache?: "default" | "no-store" | "reload" | "no-cache" | "force-cache" | "only-if-cached";
  credentials?: "omit" | "same-origin" | "include";
  mode?: "cors" | "no-cors" | "same-origin" | "navigate";
};

type FetchLike = (
  url: string,
  init?: FetchInit,
) => Promise<{
  ok: boolean;
  headers: { get(name: string): string | null };
}>;

export function desktopInstallerPublicOrigin(hostname: string): string {
  if (hostname === "nola-novel.com" || hostname === "www.nola-novel.com") {
    return "https://nola-novel.com";
  }
  return "https://dev-1.nola-novel.com";
}

export function desktopInstallerFilename(platform: DesktopInstallerPlatform): string {
  return platform === "windows" ? WINDOWS_INSTALLER_FILENAME : MAC_INSTALLER_FILENAME;
}

export function desktopInstallerUrl(
  platform: DesktopInstallerPlatform,
  hostname = typeof window === "undefined" ? "" : window.location.hostname,
): string {
  const path = `/.desktop/installers/${desktopInstallerFilename(platform)}`;
  if (hostname === "nola-novel.com" || hostname === "dev-1.nola-novel.com") {
    return path;
  }
  return `${desktopInstallerPublicOrigin(hostname)}${path}`;
}

export function isDesktopBetaDownloadOpen(nowMs: number): boolean {
  return nowMs < DESKTOP_BETA_DOWNLOAD_DEADLINE_MS;
}

function isNolaPublicHost(hostname: string): boolean {
  return hostname === "nola-novel.com" || hostname === "www.nola-novel.com" || hostname === "dev-1.nola-novel.com";
}

function parseDateHeaderMs(value: string | null): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

async function readNolaDateHeaderMs(fetchImpl: FetchLike): Promise<number | null> {
  try {
    const response = await fetchImpl("/", {
      method: "HEAD",
      cache: "no-store",
      credentials: "omit",
      mode: "cors",
    });
    return parseDateHeaderMs(response.headers.get("date"));
  } catch {
    return null;
  }
}

export async function getNowMs(
  options: {
    hostname?: string;
    fetchImpl?: FetchLike;
    deviceNowMs?: number;
  } = {},
): Promise<number> {
  const hostname = options.hostname ?? (typeof window === "undefined" ? "" : window.location.hostname);
  const fetchImpl =
    options.fetchImpl ??
    ((url: string, init?: FetchInit) => fetch(url, { credentials: "omit", mode: "cors", cache: "no-store", ...init }));
  const deviceNowMs = options.deviceNowMs ?? Date.now();

  if (isNolaPublicHost(hostname)) {
    const fromSite = await readNolaDateHeaderMs(fetchImpl);
    if (fromSite !== null) return fromSite;
  }

  return deviceNowMs;
}

export async function assertDesktopBetaDownloadOpen(
  options: {
    hostname?: string;
    fetchImpl?: FetchLike;
    deviceNowMs?: number;
  } = {},
): Promise<void> {
  const nowMs = await getNowMs(options);
  if (!isDesktopBetaDownloadOpen(nowMs)) {
    throw new Error(DESKTOP_BETA_DOWNLOAD_CLOSED);
  }
}

function startBrowserDownload(url: string, filename: string): void {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener noreferrer";
  link.referrerPolicy = "no-referrer";
  link.style.display = "none";
  document.body.append(link);
  link.click();
  link.remove();
}

export async function downloadDesktopInstaller(
  platform: DesktopInstallerPlatform,
  options: {
    hostname?: string;
    fetchImpl?: FetchLike;
    deviceNowMs?: number;
    startDownload?: (url: string, filename: string) => void;
  } = {},
): Promise<void> {
  const hostname = options.hostname ?? (typeof window === "undefined" ? "" : window.location.hostname);
  const fetchImpl =
    options.fetchImpl ??
    ((url: string, init?: FetchInit) => fetch(url, { credentials: "omit", mode: "cors", cache: "no-store", ...init }));
  await assertDesktopBetaDownloadOpen({ hostname, fetchImpl, deviceNowMs: options.deviceNowMs });
  const startDownload = options.startDownload ?? startBrowserDownload;
  startDownload(desktopInstallerUrl(platform, hostname), desktopInstallerFilename(platform));
}
